export type AlcoholStatus = 'approved' | 'coming';
export type AlcoholState = 'current' | 'paused' | 'customer' | 'cancelled';
export type Answer = 'yes' | 'no' | '';
export type SustainabilityKey = 'biodynamic' | 'organic' | 'sustainable' | 'certified' | 'vegan';
export type PromptKey = 'description' | 'shortDescription' | 'winery';

export const STATUS_LABELS: Record<AlcoholStatus, string> = { approved: 'Godkänd', coming: 'Kommande' };
export const STATE_LABELS: Record<AlcoholState, string> = {
  current: 'Aktuell',
  paused: 'Ej aktuell just nu',
  customer: 'Kundunik',
  cancelled: 'Blir inte av',
};
export const STATES: AlcoholState[] = ['current', 'paused', 'customer', 'cancelled'];

export const PAIRINGS: Array<{ key: string; label: string }> = [
  { key: 'meat', label: 'Kött' },
  { key: 'fish', label: 'Fisk' },
  { key: 'dessert', label: 'Dessert' },
  { key: 'cheese', label: 'Ost' },
  { key: 'shellfish', label: 'Skaldjur' },
  { key: 'poultry', label: 'Fågel' },
  { key: 'pork', label: 'Gris' },
  { key: 'lamb', label: 'Lamm' },
  { key: 'aperitif', label: 'Sällskapsdryck' },
  { key: 'vegetables', label: 'Grönsaker' },
];

export const SUSTAINABILITY: Array<{ key: SustainabilityKey; label: string }> = [
  { key: 'biodynamic', label: 'Biodynamiskt' },
  { key: 'organic', label: 'Ekologiskt' },
  { key: 'sustainable', label: 'Hållbar produktion' },
  { key: 'certified', label: 'Nationellt miljöcertifierat' },
  { key: 'vegan', label: 'Vegan' },
];

export const PROMPT_KEYS: PromptKey[] = ['description', 'shortDescription', 'winery'];
export const PROMPT_LABELS: Record<PromptKey, string> = {
  description: 'Beskrivning',
  shortDescription: 'Kortare beskrivning',
  winery: 'Beskrivning vingård',
};

export const NAME_LIMIT = 35;
export const SHORT_LIMIT = 300;
export const WINERY_LIMIT = 4000;
export const PROMPT_LIMIT = 20000;
export const GENERATE_INPUT_LIMIT = 6000;

export interface AlcoholFieldDef {
  key: string;
  label: string;
  maxLength: number;
  multiline?: boolean;
  kind?: 'text' | 'ean';
}

export interface AlcoholNote { at: string; by: string; text: string }

export interface AlcoholProduct {
  id: string;
  version: number;
  articleNumber: string;
  status: AlcoholStatus;
  approvedAt: string | null;
  approvedBy: string;
  state: AlcoholState;
  producer: string;
  producerKey: string;
  fields: Record<string, string>;
  pairings: string[];
  sustainability: Record<SustainabilityKey, Answer>;
  notes: AlcoholNote[];
  extra: Record<string, string>;
  source: { sheet: string; row: number } | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface AlcoholProducer {
  id: string;
  version: number;
  key: string;
  name: string;
  wineryText: string;
  productCount: number;
  updatedAt: string;
  updatedBy: string;
}

export interface AlcoholCompleteness { done: number; total: number; missing: string[] }

export type AlcoholWarning =
  | { kind: 'duplicate'; message: string; others: Array<{ id: string; articleNumber: string; name: string }> }
  | { kind: 'too_long' | 'ean_check'; message: string; field: string }
  | { kind: 'no_pairings' | 'no_description' | 'no_winery'; message: string };

export interface AlcoholSummary {
  id: string;
  version: number;
  articleNumber: string;
  status: AlcoholStatus;
  state: AlcoholState;
  producer: string;
  name: string;
  category: string;
  country: string;
  completeness: AlcoholCompleteness;
  warnings: AlcoholWarning[];
  updatedAt: string;
  athenaAssetId?: string;
}

export interface AlcoholFacet { name: string; count: number }
export interface AlcoholFacets { categories: AlcoholFacet[]; countries: AlcoholFacet[]; producers: AlcoholFacet[]; assortments: AlcoholFacet[] }
export interface AlcoholStats {
  approved: number;
  coming: number;
  notCurrent: number;
  producers: number;
  quick?: Partial<Record<Exclude<AlcoholQuick, ''>, number>>;
}

export interface AlcoholList {
  total: number;
  products: AlcoholSummary[];
  facets: AlcoholFacets;
  stats: AlcoholStats;
  fields: AlcoholFieldDef[];
}

export type AlcoholQuick = '' | 'coming' | 'notCurrent' | 'noPairings' | 'longName' | 'noWinery';

export interface AlcoholFilter {
  q: string;
  status: string;
  state: string;
  category: string;
  country: string;
  producer: string;
  assortment: string;
  quick: AlcoholQuick;
}

export interface AlcoholYearbookRow {
  sheetId: string;
  chain: string;
  rowId: string;
  statedWeek: number | null;
  announcementDate: string;
  articleName: string;
}

export interface AlcoholDetail {
  product: AlcoholProduct;
  name: string;
  producer: AlcoholProducer | null;
  completeness: AlcoholCompleteness;
  warnings: AlcoholWarning[];
  yearbook: AlcoholYearbookRow[];
}

export interface AlcoholChange { field: string; label: string; before: string; after: string }

export interface AlcoholHistoryEntry {
  id: string;
  entity: 'product' | 'producer' | 'settings';
  entityId: string;
  at: string;
  by: string;
  commandId: string;
  summary: string;
  changes: AlcoholChange[];
}

export type AlcoholPrompts = Record<PromptKey, string>;

export interface AlcoholSettings {
  id: 'default';
  version: number;
  prompts: AlcoholPrompts;
  updatedAt: string;
  updatedBy: string;
}

export interface AlcoholSuggestion {
  description?: string;
  shortDescription?: string;
  wineryText?: string;
  shortTooLong?: boolean;
}

export interface AlcoholDuplicate { id: string; articleNumber: string; name: string }

export interface AlcoholImportPreview {
  existing: number;
  total: number;
  approved: number;
  coming: number;
  withNote: number;
  producers: number;
  producersWithoutText: string[];
  textsWithoutProducts: string[];
  countries: number;
  countriesWithoutText: string[];
  duplicates: Array<{ articleNumber: string; count: number }>;
  longNames: string[];
  unparsed: string[];
  unknownColumns: string[];
  skippedRows: string[];
  alreadyThere: number;
  producersAlreadyThere: number;
}
